///
/// EarthBrowser
///
/// Created by Matt Giger on 12/20/2024
/// Copyright (c) 2024 Geodata Labs
///

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRevokeDeviceMutation } from "@/redux/userApi"
import { getDeviceId } from "@/utils/deviceId"
import type { DeviceRecord } from "@shared-types/device"
import { formatDistanceToNow } from "date-fns"
import {
	AppleIcon,
	LaptopIcon,
	MonitorIcon,
	MoreHorizontal,
	SmartphoneIcon,
	TabletIcon,
} from "lucide-react"
import { useState } from "react"

// OS Icon component
function OSIcon({ osName }: { osName: string }) {
	const os = osName.toLowerCase()

	if (os.includes('windows')) {
		return <LaptopIcon className="h-5 w-5 text-teal-600" />
	} else if (os.includes('mac') || os.includes('darwin')) {
		return <AppleIcon className="h-5 w-5 text-teal-600" />
	} else if (os.includes('linux')) {
		return <MonitorIcon className="h-5 w-5 text-teal-600" />
	} else if (os.includes('android')) {
		return <SmartphoneIcon className="h-5 w-5 text-teal-600" />
	} else if (os.includes('ios') || os.includes('iphone') || os.includes('ipad')) {
		return <TabletIcon className="h-5 w-5 text-teal-600" />
	}

	return <MonitorIcon className="h-5 w-5 text-teal-600" />
}

// Browser Icon component (using colored circles to represent different browsers)
function BrowserIcon({ deviceName }: { deviceName: string }) {
	const browser = deviceName.toLowerCase()

	if (browser.includes('chrome')) {
		return <div className="w-4 h-4 rounded-full bg-gradient-to-br from-red-500 via-yellow-500 to-green-500" />
	} else if (browser.includes('firefox')) {
		return <div className="w-4 h-4 rounded-full bg-gradient-to-br from-orange-600 to-orange-400" />
	} else if (browser.includes('safari')) {
		return <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400" />
	} else if (browser.includes('edge')) {
		return <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-600 to-teal-500" />
	} else if (browser.includes('opera')) {
		return <div className="w-4 h-4 rounded-full bg-gradient-to-br from-red-600 to-red-400" />
	}

	return <div className="w-4 h-4 rounded-full bg-gradient-to-br from-gray-500 to-gray-400" />
}

function lastActiveText(device: DeviceRecord): string {
	if (!device.last_used_at) {
		return "Unknown"
	}
	try {
		return formatDistanceToNow(new Date(device.last_used_at), { addSuffix: true })
	} catch {
		return "Unknown"
	}
}

interface DevicesTableProps {
	data: DeviceRecord[]
}

export function DevicesTable({ data }: DevicesTableProps) {
	const { toast } = useToast()
	const [revokeDevice] = useRevokeDeviceMutation()
	const [deviceToRevoke, setDeviceToRevoke] = useState<DeviceRecord | null>(null)
	const currentDeviceId = getDeviceId()

	function isCurrentDevice(device: DeviceRecord): boolean {
		return device.device_fingerprint === currentDeviceId
	}

	async function handleRevoke() {
		const device = deviceToRevoke
		setDeviceToRevoke(null)
		if (!device?.id) {
			return
		}
		try {
			await revokeDevice(device.id).unwrap()
			toast({
				title: "Device Removed",
				description: `${device.os_name} device and its sessions have been signed out`,
			})
		} catch (error) {
			console.error("Revoke device error:", error)
			toast({
				title: "Failed to Remove Device",
				variant: "destructive",
				description: "Unable to remove the device. Please try again.",
			})
		}
	}

	return (
		<div className="w-full">
			<Table>
				<TableHeader>
					<TableRow className="border-teal-500/10 hover:bg-transparent">
						<TableHead>Device</TableHead>
						<TableHead>Browser</TableHead>
						<TableHead>Last Active</TableHead>
						<TableHead className="w-12"></TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{data.length === 0 ? (
						<TableRow>
							<TableCell colSpan={4} className="h-24 text-center">
								None
							</TableCell>
						</TableRow>
					) : (
						data.map((device, index) => (
							<TableRow
								key={device.id ?? index}
								className="border-teal-500/10 hover:bg-teal-500/5 transition-colors"
							>
								<TableCell>
									<div className="flex items-center gap-2">
										<OSIcon osName={device.os_name || 'Unknown'} />
										<div className="flex flex-col">
											<span className="text-sm font-medium flex items-center gap-1.5">
												{device.os_name || 'Unknown'}
												{isCurrentDevice(device) && (
													<Badge className="shrink-0 whitespace-nowrap border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 px-1.5 py-0 text-[10px] font-medium" variant="outline">
														This device
													</Badge>
												)}
											</span>
											{device.os_version && <span className="text-xs text-muted-foreground">{device.os_version}</span>}
										</div>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<BrowserIcon deviceName={device.device_name || 'Unknown'} />
										<div className="flex flex-col">
											<span className="text-sm font-medium">{device.device_name || 'Unknown'}</span>
											{device.device_version && <span className="text-xs text-muted-foreground">v{device.device_version}</span>}
										</div>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex flex-col">
										<span className="text-sm">{lastActiveText(device)}</span>
										{device.ip_address && <span className="text-xs text-muted-foreground font-mono break-all">{device.ip_address}</span>}
									</div>
								</TableCell>
								<TableCell className="pl-0 pr-3 text-right">
									{!isCurrentDevice(device) && device.id && (
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-full">
													<MoreHorizontal className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem
													className="text-red-600 focus:text-red-600"
													onClick={() => setDeviceToRevoke(device)}
												>
													Remove Device
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									)}
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>

			{/* Revoke confirmation */}
			<AlertDialog open={deviceToRevoke !== null} onOpenChange={(open) => { if (!open) setDeviceToRevoke(null) }}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove this device?</AlertDialogTitle>
						<AlertDialogDescription>
							{deviceToRevoke && (deviceToRevoke.os_name || deviceToRevoke.device_name)
								? [deviceToRevoke.os_name, deviceToRevoke.device_name].filter(Boolean).join(" · ")
								: "This device"} will be
							signed out and removed from your trusted devices. It can be re-added the next time you sign in from it.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							className="bg-red-600 text-white hover:bg-red-700"
							onClick={handleRevoke}
						>
							Remove Device
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
